import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Button from 'UIKit/Button'
import Image from 'UIKit/Image'
import Text from 'UIKit/Text'
import { BonusTagEnums, CommonBonus, WageringConditionCommonBonus } from 'config/types/bonus'
import { UserBonus, WageringConditionBonus } from 'config/types/bonus/userBonus'
import useModal from 'hooks/useModal'
import { RowBetween } from 'layout/Components/Row'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { buildBonusName } from 'views/BonusCenter/hooks'
import BonusDetailsModal from '../../BonusDetailsModal'

interface UpcomingBonusSectionProps {
  bonus: UserBonus | CommonBonus
  isLastItem: boolean
  isCurrentBonus?: boolean
}

const UpcomingBonusSection: React.FC<UpcomingBonusSectionProps> = ({ bonus, isLastItem, isCurrentBonus }) => {
  const { t } = useTranslation()
  const [onPresentBonusDetailModal] = useModal(BonusDetailsModal)

  return (
    <Box position="relative" maxWidth="240px" mx="auto">
      <StyledImage $isLastItem={isLastItem}>
        <Image
          src={bonus.info.image}
          alt="image-bonus-upcoming"
          width={isLastItem ? 120 : 60}
          height={isLastItem ? 120 : 60}
        />
      </StyledImage>

      <CurrentBonusContainer $isLastItem={isLastItem} p="20px">
        <Flex alignItems="center">
          <Text fontSize="12px" color={isCurrentBonus ? 'strokeAlt3' : 'textSubtle'} mb="4px">
            {t(isCurrentBonus ? 'Current Bonus' : 'Upcoming Bonus')}
          </Text>
        </Flex>

        <Flex alignItems="center" mb="12px">
          <Text fontSize="16px" fontWeight={800} lineHeight="normal" letterSpacing="-0.32px" textAlign="center">
            {buildBonusName(t, bonus.info?.name)[0]}
          </Text>
        </Flex>

        {(bonus instanceof WageringConditionCommonBonus || bonus instanceof WageringConditionBonus) && (
          <StyledContentBonus width="100%">
            <RowBetween>
              <Text fontSize="12px" color="textSubtle" lineHeight="normal" letterSpacing="-0.24px">
                {t('Min Deposit')}
              </Text>

              <Text fontWeight="700" fontSize="12px" ml="2px">
                {bonus.tags.includes(BonusTagEnums.NoDeposit) ? (
                  <Trans>No deposit</Trans>
                ) : (
                  `$${getFullDisplayBalance(bonus.minDepositBigAmount)}`
                )}
              </Text>
            </RowBetween>
            <RowBetween>
              <Text fontSize="12px" color="textSubtle" lineHeight="normal" letterSpacing="-0.24px">
                {t('Wager')}
              </Text>

              <Text fontWeight="700" fontSize="12px">
                x{bonus.wagerMultiplier}
              </Text>
            </RowBetween>
          </StyledContentBonus>
        )}

        <StyledBonusDetail
          mt="12px"
          onClick={() =>
            onPresentBonusDetailModal({
              bonus,
            })
          }
        >
          <Text color="textAlt1" fontSize="12px" mr="6px">
            <Trans>Bonus Details</Trans>
          </Text>
          <Icons.ChevronRightIcon width="5px" />
        </StyledBonusDetail>
      </CurrentBonusContainer>

      <StyledUpcomingBonusArrow
        className="arrow-down"
        justifyContent="center"
        width="100%"
        height="56px"
        my="12px"
        $isLastItem={isLastItem}
      >
        <Image src={'/images/bonus/triple-down-arrow.png'} alt="triple-down-arrow" width={60} height={60} />
      </StyledUpcomingBonusArrow>
    </Box>
  )
}

const StyledBonusDetail = styled(Flex)`
  cursor: pointer;
  justify-content: center;
  align-items: center;

  svg {
    fill: ${({ theme }) => theme.colors.textAlt1};
  }

  &:hover {
    ${Text} {
      color: ${({ theme }) => theme.colors.text} !important;
    }

    svg {
      fill: ${({ theme }) => theme.colors.text};
    }
  }
`

export const StyledButton = styled(Button)<{ fontSize?: any; primary?: boolean }>`
  width: 100%;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.12);
  height: 44px;
  padding: 15px 10px;
  font-size: ${({ fontSize }) => fontSize || '10px'};
  font-weight: 600;

  white-space: nowrap;
  text-align: center;

  &:hover {
    transform: scale(1.025) !important;
  }

  ${({ primary, theme }) =>
    primary &&
    css`
      background: ${theme.colors.primaryAlt};
      border-color: ${theme.colors.primaryAlt};
    `}

  &:disabled {
    background: rgba(255, 255, 255, 0.08);

    ${({ primary, theme }) =>
      primary &&
      css`
        background: ${theme.colors.primaryDisabled};
      `}
  }
`

const CurrentBonusContainer = styled(Box)<{ $isLastItem?: boolean }>`
  margin-top: 40px;
  padding: 32px 18px 20px 18px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0) 70%);

  ${({ $isLastItem }) =>
    $isLastItem &&
    css`
      margin-top: 85px;
    `};
`

const StyledImage = styled(Box)<{ $isLastItem?: boolean }>`
  position: absolute;
  top: 0%;
  left: 50%;
  transform: translate(-50%, -50%);

  img {
    min-width: 60px;
    min-height: 60px;
  }

  ${({ $isLastItem }) =>
    $isLastItem &&
    css`
      top: -15%;
    `};
`

const StyledContentBonus = styled(Box)`
  gap: 8px;
  display: flex;
  flex-direction: column;
`

const StyledUpcomingBonusArrow = styled(Flex)<{ $isLastItem?: boolean }>`
  display: flex;

  ${({ $isLastItem }) =>
    $isLastItem &&
    css`
      display: none;
    `};
`

export default UpcomingBonusSection
